/**
 * EditorialGuidePage — /{city}/guides/{slug}
 * ===========================================
 * DesignMyNight-style magazine layout. Each venue is a full-width
 * editorial card with a scrollable photo carousel, a partner-state
 * banner (next upcoming event), live-offering chips (Experiences /
 * Reservations), and a single in-funnel CTA — "Book Here" when the
 * venue has bookable inventory on Eventism, "View venue" otherwise.
 *
 * Every link is in-app (we never send the buyer back to a 3rd-party
 * site). The card's CTA lands on `/events/{city}/venues/{venue_slug}`
 * — the canonical Eventism venue page that already aggregates events,
 * experiences, and reservations for that venue.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  MapPin, Star, Calendar, ChevronRight, ChevronLeft, Sparkles, Clock,
  Wine, CheckCircle2, ArrowRight, Loader2, Utensils, Compass, Ticket,
  Award, Quote, Eye, Instagram, Facebook, Youtube, Globe, Music2,
  ExternalLink,
} from 'lucide-react';
import SEO from '../components/SEO';
import { GuideEngagementBar } from '../components/GuideEngagementBar';
import { organizationLD, websiteLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';

const titleize = (s = '') =>
  String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const formatTime = (t) => {
  if (!t) return '';
  // "18:30" → "6:30 PM"
  const [hh, mm] = (t || '').split(':');
  const h = parseInt(hh, 10);
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${mm} ${ampm}`;
};

const venueDetailUrl = (city, slug) => `/${(city || 'denver').toLowerCase()}/venues/${slug}`;

// Map a hosted_event row to the canonical Eventism detail URL based on its
// `kind`. Mirrors the routes registered in App.js (/events, /ex, /res).
const KIND_URL = { event: 'events', experience: 'ex', reservation: 'res' };
const eventDetailUrl = (event, fallbackCity) => {
  if (!event) return null;
  const seg = KIND_URL[event.kind] || 'events';
  const c = (event.city || fallbackCity || 'denver').toLowerCase();
  return `/${seg}/${c}/${event.slug || event.event_id}`;
};

// Vibe tag URL — links each tag to the city's venues directory,
// filtered by that tag. Clicking a tag on the guide leads to every
// other venue in the city with the same vibe (not to an events list),
// which is what turns the guide into a true venue-discovery hub.
// NOTE: we pass the raw tag (URL-encoded) — NOT a slug — because
// venue tags in the DB use spaces ("natural wine"), and the backend
// filter does an exact case-insensitive match.
const tagSearchUrl = (city, tag) =>
  `/${(city || 'denver').toLowerCase()}/venues?tag=${encodeURIComponent(
    String(tag).toLowerCase().trim(),
  )}`;

// Format the ticket price range surfaced on the editorial banner CTA.
// "from $X" when min==max OR only min is known; "$X–$Y" when both differ.
const priceRangeLabel = (ev) => {
  const min = Number(ev?.ticket_price_min);
  const max = Number(ev?.ticket_price_max);
  // External-link listings have no Eventism price — never imply "Free".
  if (ev?.ticket_type === 'external') {
    if (ev.external_price_label) return ev.external_price_label;
    if (Number.isFinite(min) && min > 0) {
      return Number.isFinite(max) && max > min ? `from $${min}–$${max}` : `from $${min}`;
    }
    return 'Get Tickets';
  }
  if (Number.isFinite(min) && min === 0) return 'Free — Reserve';
  if (Number.isFinite(min) && Number.isFinite(max) && max > min) return `from $${min}–$${max}`;
  if (Number.isFinite(min)) return `from $${min}`;
  return 'Buy Tickets';
};

// Human-readable "time remaining" formatter for the live countdown under
// the Buy CTA. Returns null once the sale has ended so the consumer can
// hide the countdown gracefully.
const countdownLabel = (targetIso) => {
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return null;
  const now = Date.now();
  const delta = target - now;
  if (delta <= 0) return null;
  const days = Math.floor(delta / (24 * 60 * 60 * 1000));
  const hours = Math.floor((delta % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((delta % (60 * 60 * 1000)) / (60 * 1000));
  if (days > 1)  return `${days}d ${hours}h left to buy`;
  if (days === 1) return `1 day ${hours}h left to buy`;
  if (hours > 1) return `${hours}h ${mins}m left to buy`;
  if (hours === 1) return `1h ${mins}m left to buy`;
  if (mins > 0)   return `${mins}m left to buy`;
  return 'Closing now';
};

// Color tokens for scarcity badges — mirrors `ticket_badges.py` palette.
const BADGE_COLOR = {
  sold_out:        'bg-red-500 text-white',
  only_n_left:     'bg-orange-500 text-white',
  almost_sold_out: 'bg-orange-500 text-white',
  last_chance:     'bg-red-500 text-white',
  on_fire:         'bg-red-500 text-white',
  selling_fast:    'bg-orange-500 text-white',
  just_added:      'bg-purple-500 text-white',
};

/**
 * BuyTicketsCTA — right-side stack on the editorial guide event banner.
 *
 *   ┌──────────────────────────┐
 *   │ [ Buy Tickets Now ]      │  ← gradient pill, price range inline
 *   │ from $25                 │
 *   │ 2d 4h left to buy        │  ← live countdown (updates every 60s)
 *   │ [ Selling Fast ]         │  ← scarcity badge from ticket_badges
 *   └──────────────────────────┘
 *
 * The countdown re-renders every 60s — cheap enough for a handful of
 * cards on screen. We deliberately skip per-second updates: the urgency
 * we're communicating is human-readable (days/hours), not auction-clock.
 */
function BuyTicketsCTA({ nextEvent, venueId }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const countdown = countdownLabel(nextEvent?.sale_end);
  const badge = nextEvent?.badge;
  const price = priceRangeLabel(nextEvent);

  return (
    <div className="flex flex-col items-end gap-1.5 text-right">
      <span
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 text-white text-xs sm:text-sm font-bold uppercase tracking-widest shadow-md group-hover:scale-[1.02] transition-transform"
        data-testid={`venue-buy-cta-${venueId}`}
      >
        <Ticket className="h-3.5 w-3.5" />
        Buy Tickets Now
        <span className="font-semibold normal-case tracking-normal opacity-90">
          {price}
        </span>
      </span>
      {countdown && (
        <span
          className="text-[11px] sm:text-xs text-amber-300 font-semibold inline-flex items-center gap-1"
          data-testid={`venue-buy-countdown-${venueId}`}
        >
          <Clock className="h-3 w-3" /> {countdown}
        </span>
      )}
      {badge && (
        <Badge
          className={`${BADGE_COLOR[badge.code] || 'bg-fuchsia-500 text-white'} text-[10px] font-bold border-0 uppercase tracking-widest`}
          data-testid={`venue-buy-scarcity-${venueId}`}
        >
          {badge.text}
        </Badge>
      )}
    </div>
  );
}

// Build the JSON-LD blob that powers Google rich-snippets for this guide.
function buildGuideJsonLd({ guide, venues, city, slug }) {
  // BuyTicketsCTA is defined below the buildGuideJsonLd helper.
  const url = `${SITE_URL}/${city}/guides/${slug}`;
  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: titleize(city), item: `${SITE_URL}/${city}` },
      { '@type': 'ListItem', position: 3, name: 'Guides', item: `${SITE_URL}/${city}/guides` },
      { '@type': 'ListItem', position: 4, name: guide.title, item: url },
    ],
  };
  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: guide.title,
    numberOfItems: venues.length,
    itemListElement: venues.map((v, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'LocalBusiness',
        name: v.name,
        address: v.address || `${titleize(v.city || city)}, ${v.state || ''}`,
        url: `${SITE_URL}${venueDetailUrl(city, v.slug)}`,
        image: v.image_url || (v.photos || [])[0] || undefined,
        aggregateRating: v.avg_rating
          ? { '@type': 'AggregateRating', ratingValue: v.avg_rating, ratingCount: 50 }
          : undefined,
      },
    })),
  };
  const faqs = (guide.faqs || []).filter((f) => f.q && f.a);
  const faqPage = faqs.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;
  const article = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.meta_description,
    image: guide.cover_image || undefined,
    datePublished: guide.created_at,
    dateModified: guide.updated_at || guide.last_verified_at,
    author: {
      '@type': guide.last_edited_by_name || guide.author_name ? 'Person' : 'Organization',
      name: guide.last_edited_by_name || guide.author_name || 'Eventism Editors',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Eventism',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/eventism-logo.png` },
    },
    mainEntityOfPage: url,
  };
  return [organizationLD(), websiteLD(), breadcrumbs, itemList, article, ...(faqPage ? [faqPage] : [])];
}

export default function EditorialGuidePage() {
  const { city, slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [authorPromo, setAuthorPromo] = useState(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    // Admins get to preview drafts — pass the token so the backend can
    // detect an admin session and bypass the is_published gate.
    const t = localStorage.getItem('eventism_token');
    const headers = t ? { Authorization: `Bearer ${t}` } : {};
    axios.get(`${API}/public/guides/${city}/${slug}`, { headers })
      .then((res) => { setData(res.data); setLoading(false); })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        setLoading(false);
      });
  }, [city, slug]);

  // Author Auto-Promoter — attribute this reader to the guest author
  // (same 30-day cookie the street-team landing pages use).
  useEffect(() => {
    const guideId = data?.guide?.guide_id;
    if (!guideId) return;
    axios.get(`${API}/editorial-guides/${guideId}/author-promo`)
      .then((r) => {
        const code = r.data?.code;
        if (!code) return;
        const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `eventism_promo=${encodeURIComponent(code)}; expires=${expires}; path=/; SameSite=Lax`;
        try { localStorage.setItem('eventism_promo', JSON.stringify({ code, ts: Date.now() })); } catch { /* ignore */ }
        axios.post(`${API}/author-promo/track-click`, { code }).catch(() => {});
        setAuthorPromo(r.data);
      })
      .catch(() => {});
  }, [data?.guide?.guide_id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="guide-loading">
        <Loader2 className="h-7 w-7 animate-spin text-fuchsia-300" />
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="guide-not-found">
        <Card className="border-border max-w-md">
          <CardContent className="p-8 text-center space-y-3">
            <Sparkles className="h-8 w-8 mx-auto text-fuchsia-300" />
            <p className="text-lg font-bold font-['Space_Grotesk']">Guide not found</p>
            <p className="text-sm text-muted-foreground">
              This editorial guide isn&apos;t live yet — or the URL might be off.
            </p>
            <Link to="/find-events">
              <Button variant="outline">Browse what&apos;s on</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { guide, venues, events, related, preview } = data;
  const eventCards = data.event_cards || [];
  const isEventGuide = (data.subject_type || guide.subject_type || 'venues') === 'events';
  const lastUpdated = guide.last_verified_at || guide.updated_at;
  const jsonLd = buildGuideJsonLd({ guide, venues, city, slug });

  return (
    <div className="min-h-screen bg-background pb-16" data-testid="editorial-guide-page">
      <SEO
        title={`${guide.seo_title || guide.title} | Eventism`}
        description={guide.meta_description}
        canonical={`/${city}/guides/${slug}`}
        image={guide.cover_image}
        jsonLd={jsonLd}
      />

      {preview && !guide.is_published && (
        <div
          className="sticky top-16 z-30 bg-amber-500/95 text-black text-center text-xs font-bold uppercase tracking-widest py-2 px-4 backdrop-blur"
          data-testid="guide-preview-banner"
        >
          Admin preview — this guide is in <span className="px-1.5 py-0.5 rounded bg-black/15 mx-1">{guide.status || 'draft'}</span> · not visible to the public yet
        </div>
      )}

      {/* ====== EDITORIAL COVER HERO ====== */}
      <header className="relative pt-24 pb-14 overflow-hidden" data-testid="guide-hero">
        {guide.cover_image && (
          <>
            <img
              src={guide.cover_image}
              alt={guide.title}
              className="absolute inset-0 w-full h-full object-cover opacity-30"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/75 to-background" />
          </>
        )}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(217,70,239,0.18),transparent_55%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground flex flex-wrap gap-2 mb-6" aria-label="Breadcrumb" data-testid="guide-breadcrumbs">
            <Link to="/" className="hover:text-foreground">Eventism</Link>
            <span>/</span>
            <Link to={`/${city}`} className="hover:text-foreground">{titleize(city)}</Link>
            <span>/</span>
            <span className="text-foreground/80">Guides</span>
          </nav>

          <Badge
            variant="outline"
            className="border-amber-400/30 text-amber-300 bg-amber-500/5 px-3 py-1 text-[11px] uppercase tracking-widest font-bold rounded-full mb-4"
          >
            <Wine className="h-3 w-3 mr-1.5 inline" /> {guide.category_label || 'Editorial Guide'}
          </Badge>

          <h1
            className="font-bold font-['Space_Grotesk'] tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mb-4"
            data-testid="guide-h1"
          >
            {guide.h1 || guide.title}
          </h1>

          <div className="flex items-center gap-x-5 gap-y-2 text-xs text-muted-foreground flex-wrap">
            <AuthorByline
              guide={guide}
              editorProfile={data?.editor_profile}
            />
            {lastUpdated && (
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-emerald-400" />
                Last verified {new Date(lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Editor-curated
            </span>
          </div>
          <GuideEngagementBar city={city} slug={slug} title={guide.h1 || guide.title} />
          {authorPromo && (
            <p className="mt-3 text-[11px] text-muted-foreground/70" data-testid="author-promo-disclosure">
              ♥ {authorPromo.author_name} may earn a commission on bookings made through this guide.
            </p>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
        {/* ====== INTRO ====== */}
        {guide.intro_html && (
          <div
            className="prose prose-invert max-w-none text-base sm:text-lg text-foreground/85 leading-relaxed [&_p]:mb-4 [&_strong]:text-foreground [&_em]:text-foreground"
            dangerouslySetInnerHTML={{ __html: guide.intro_html }}
            data-testid="guide-intro"
          />
        )}

        {/* ====== EDITORIAL VENUE LIST / EVENT CARDS ====== */}
        {isEventGuide ? (
          <section data-testid="guide-event-cards" className="space-y-12">
            <div className="flex items-baseline gap-3 border-b border-border pb-4">
              <p className="text-[11px] uppercase tracking-widest font-bold text-indigo-300">The list</p>
              <span className="text-xs text-muted-foreground">
                {eventCards.length} editorial card{eventCards.length === 1 ? '' : 's'}
              </span>
            </div>
            {eventCards.length === 0 ? (
              <Card className="border-dashed border-2 border-border">
                <CardContent className="p-10 text-center">
                  <p className="text-sm text-muted-foreground">
                    This guide is being written. Cards land here as our editors publish them.
                  </p>
                </CardContent>
              </Card>
            ) : (
              eventCards.map((card, i) => (
                <EditorialEventCard
                  key={card.card_id}
                  card={card}
                  index={i + 1}
                  city={city}
                />
              ))
            )}
          </section>
        ) : (
          <section data-testid="guide-venues" className="space-y-12">
            <div className="flex items-baseline gap-3 border-b border-border pb-4">
              <p className="text-[11px] uppercase tracking-widest font-bold text-amber-300">The list</p>
              <span className="text-xs text-muted-foreground">
                {venues.length} editor-verified spot{venues.length === 1 ? '' : 's'}
              </span>
            </div>

            {venues.length === 0 ? (
              <Card className="border-dashed border-2 border-border">
                <CardContent className="p-10 text-center">
                  <p className="text-sm text-muted-foreground">
                    Our editors are still scouting. New venues land here every week.
                  </p>
                </CardContent>
              </Card>
            ) : (
              venues.map((v, i) =>
                <EditorialVenueCard
                  key={v.venue_id}
                  venue={v}
                  index={i + 1}
                  city={city}
                  addendumFields={guide?.venue_addendum_fields || []}
                  addendumData={(guide?.venue_addendum || {})[v.venue_id] || {}}
                  isEditorPick={(guide?.editor_picks || []).includes(v.venue_id)}
                />,
              )
            )}
          </section>
        )}

        {/* ====== UPCOMING EVENTS RAIL (venue-guide only) ====== */}
        {!isEventGuide && events.length > 0 && (
          <section data-testid="guide-events" className="space-y-5">
            <div className="flex items-baseline gap-3 border-b border-border pb-4">
              <p className="text-[11px] uppercase tracking-widest font-bold text-amber-300">Coming up</p>
              <span className="text-xs text-muted-foreground">In {titleize(city)}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {events.map((e) => <EventCard key={e.event_id} event={e} />)}
            </div>
          </section>
        )}

        {/* ====== FAQ ====== */}
        {(guide.faqs || []).length > 0 && (
          <section data-testid="guide-faq" className="space-y-3">
            <div className="border-b border-border pb-4">
              <p className="text-[11px] uppercase tracking-widest font-bold text-amber-300">FAQ</p>
            </div>
            {(guide.faqs || []).map((f, i) => (
              <details
                key={i}
                className="group border border-border rounded-xl bg-card/40 p-5 open:bg-card/60 transition-colors"
                data-testid={`guide-faq-${i}`}
              >
                <summary className="cursor-pointer font-semibold text-base flex items-center justify-between gap-3">
                  {f.q}
                  <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90 text-fuchsia-300 flex-shrink-0" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </section>
        )}

        {/* ====== RELATED ====== */}
        {related && related.length > 0 && (
          <section data-testid="guide-related" className="space-y-5">
            <div className="border-b border-border pb-4">
              <p className="text-[11px] uppercase tracking-widest font-bold text-amber-300">More {titleize(city)} guides</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/${r.city}/guides/${r.slug}`}
                  className="group block p-4 rounded-xl border border-border hover:border-fuchsia-400/50 transition-colors"
                  data-testid={`related-guide-${r.slug}`}
                >
                  <p className="text-[10px] uppercase font-bold tracking-widest text-amber-300 mb-1">
                    {r.category_label || 'Guide'}
                  </p>
                  <p className="font-semibold group-hover:text-fuchsia-300 transition-colors">
                    {r.title}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ====== FINAL CTA — partner acquisition ====== */}
        <section>
          <Card className="border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/10 via-amber-500/5 to-indigo-500/10">
            <CardContent className="p-7 text-center space-y-3">
              <p className="text-xl font-bold font-['Space_Grotesk']">Run a venue in {titleize(city)}?</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Get your spot on the next edition of this guide. Eventism editors curate from
                live, verified, bookable venues — list yours free.
              </p>
              <Link to="/organizations/apply">
                <Button className="h-11 px-6 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0">
                  Claim your venue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* ====== CITATION BLOCK — Phase 4 GEO (sr-only, hidden from users) ====== */}
        {/* Kept in the DOM so AI crawlers can still lift a clean
            attributed citation, but visually hidden so it doesn't
            clutter the editorial UX. */}
        <section
          data-testid="guide-citation"
          aria-hidden="false"
          className="sr-only"
        >
          <p>Cite this page</p>
          <p>
            {guide.last_edited_by_name || guide.author_name || 'Eventism Editors'}. &ldquo;{guide.title}&rdquo;.{' '}
            {lastUpdated && <>Last verified {new Date(lastUpdated).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. </>}
            {SITE_URL}/{city}/guides/{slug}
          </p>
          <p>
            Machine-readable facts (JSON):{' '}
            <a
              href={`${SITE_URL}/api/entities/guide/${city}/${slug}`}
              data-testid="guide-citation-api-link"
            >
              {`/api/entities/guide/${city}/${slug}`}
            </a>
          </p>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditorialVenueCard — DesignMyNight-style magazine layout.
//
// Top: large numbered photo carousel (horizontally scrollable, swipeable).
// Below the photo: partner banner for the next upcoming event (if any).
// Then: H2 name, neighborhood + price + rating, description, vibe tags,
// live-offering chips, and a single in-funnel CTA ("Book Here" or "View
// venue") that routes to /events/{city}/venues/{slug}.
// ---------------------------------------------------------------------------
function EditorialVenueCard({ venue, index, city, addendumFields = [], addendumData = {}, isEditorPick = false }) {
  // Build a clean photo list: dedupe and prefer `photos[]` over a single
  // image_url. Falls back to a single-image carousel so the layout never
  // collapses.
  const photos = (() => {
    const list = Array.isArray(venue.photos) && venue.photos.length ? venue.photos : (venue.image_url ? [venue.image_url] : []);
    return Array.from(new Set(list));
  })();

  const offerings = venue.offerings || {};
  const isPartner = !!offerings.is_partner;
  const nextEvent = offerings.next_event;
  const detailUrl = venueDetailUrl(city, venue.slug);
  const nextEventUrl = eventDetailUrl(nextEvent, city);

  // Pick the most-specific live-offering badge for the photo top-left chip.
  // Priority: events > experiences > reservations (events are the highest-
  // intent buyer signal).
  const photoBadge = (() => {
    if (offerings.events > 0) {
      return { icon: Ticket, label: `${offerings.events} event${offerings.events > 1 ? 's' : ''} on sale` };
    }
    if (offerings.experiences > 0) {
      return { icon: Compass, label: `${offerings.experiences} experience${offerings.experiences > 1 ? 's' : ''} bookable` };
    }
    if (offerings.reservations > 0) {
      return { icon: Utensils, label: 'Reservations open' };
    }
    return null;
  })();

  return (
    <article
      className="space-y-4"
      data-testid={`venue-card-${venue.venue_id}`}
    >
      {/* ---- Number + name kicker (above the photo, magazine feel) ---- */}
      <div className="flex items-baseline gap-3">
        <span
          className="text-4xl sm:text-5xl font-bold font-['Space_Grotesk'] tracking-tight text-fuchsia-300/70 tabular-nums"
          aria-hidden="true"
        >
          {String(index).padStart(2, '0')}
        </span>
        <div className="min-w-0 flex-1">
          <Link
            to={detailUrl}
            className="inline-block group/name"
            data-testid={`venue-card-name-link-${venue.venue_id}`}
          >
            <h2
              className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] tracking-tight leading-tight group-hover/name:text-fuchsia-300 transition-colors"
              data-testid={`venue-card-name-${venue.venue_id}`}
            >
              {venue.name}
            </h2>
          </Link>
          {isEditorPick && (
            <span
              className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-amber-500 text-black shadow-sm shadow-amber-500/30"
              data-testid={`editor-pick-badge-${venue.venue_id}`}
            >
              <Star className="h-3 w-3 fill-black" /> Editor&apos;s Pick
            </span>
          )}
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
            {venue.neighborhood && (
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {venue.neighborhood}, {titleize(venue.city || city)}</span>
            )}
            {venue.price_band && <span className="font-semibold text-foreground/70">{venue.price_band}</span>}
            {venue.avg_rating && (
              <span className="flex items-center gap-0.5 text-amber-300 font-semibold">
                <Star className="h-3 w-3 fill-current" /> {Number(venue.avg_rating).toFixed(1)}
              </span>
            )}
            {venue.is_featured && (
              <span className="flex items-center gap-1 text-amber-300 font-semibold uppercase tracking-widest text-[10px]">
                <Star className="h-3 w-3 fill-current" /> Editors&apos; Pick
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ---- Partner banner (upcoming event) — placed ABOVE the photo
            gallery so the bookable inventory is the first thing a reader
            sees on a partner venue card. Top-of-funnel conversion lever.

            Layout: event name + date on the LEFT, "Buy Tickets Now from $X"
            CTA pill on the RIGHT with countdown + scarcity badge underneath.
            Mirrors the urgency stack we use on event listing cards. */}
      {nextEvent && nextEventUrl && (
        <Link
          to={nextEventUrl}
          className="block rounded-xl border border-fuchsia-500/40 bg-gradient-to-r from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15 hover:from-fuchsia-500/25 transition-colors p-3 sm:p-4"
          data-testid={`venue-next-event-banner-${venue.venue_id}`}
        >
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
            {/* LEFT — event identity (name + date) */}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm sm:text-base truncate" data-testid={`venue-next-event-name-${venue.venue_id}`}>
                {nextEvent.name}
              </p>
              <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
                <Calendar className="h-3 w-3 inline mr-1 text-fuchsia-300" />
                {formatDate(nextEvent.event_date)}
                {nextEvent.event_time ? ` · ${formatTime(nextEvent.event_time)}` : ''}
              </p>
            </div>

            {/* RIGHT — CTA + urgency stack */}
            <BuyTicketsCTA nextEvent={nextEvent} venueId={venue.venue_id} />
          </div>
        </Link>
      )}

      {/* ---- Photo carousel ---- */}
      <PhotoCarousel photos={photos} alt={venue.name} venueId={venue.venue_id} badge={photoBadge} detailUrl={detailUrl} />

      {/* ---- Editorial addendum: labeled lines under the photos ---- */}
      <VenueAddendum
        fields={addendumFields}
        data={addendumData}
        venueId={venue.venue_id}
      />

      {/* ---- Body ---- */}
      <div className="space-y-3">
        {venue.description && (
          <p className="text-base sm:text-lg leading-relaxed text-foreground/85">
            {venue.description}
          </p>
        )}

        {/* Live-offering chips: ONLY when something is actually bookable */}
        <OfferingChips
          city={city}
          offerings={offerings}
          amenities={venue.amenities}
          vibe_tags={venue.vibe_tags}
          venueId={venue.venue_id}
        />

        {/* Single in-funnel CTA */}
        <div className="pt-2">
          <Link to={detailUrl} data-testid={`venue-cta-${venue.venue_id}`}>
            <Button
              size="lg"
              className={`h-11 px-7 font-bold border-0 ${
                isPartner
                  ? 'bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white shadow-lg shadow-fuchsia-500/20'
                  : 'bg-foreground text-background hover:bg-foreground/85'
              }`}
            >
              {isPartner ? 'Book Here' : 'View venue'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Divider between cards (no border on the last one) */}
      <hr className="border-border/40 mt-10 last:hidden" />
    </article>
  );
}

// ---------------------------------------------------------------------------
// AuthorByline — avatar + name with a hover popover showing the editor's
// blurb + social links. Trust signal for readers: a real human curates this.
// The popover renders via createPortal into document.body so it floats
// above the hero (which uses overflow: hidden for its parallax bg).
// ---------------------------------------------------------------------------
function AuthorByline({ guide, editorProfile }) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const timerRef = useRef(null);
  const anchorRef = useRef(null);
  const name = editorProfile?.display_name
    || guide.last_edited_by_name
    || guide.author_name
    || 'Eventism Editors';
  const avatar = editorProfile?.avatar_url;
  const hasCard = !!(editorProfile && (editorProfile.blurb || Object.keys(editorProfile.social_links || {}).length));

  const measure = () => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    // Position the card 6px below the byline. Fixed → viewport coords.
    setPos({ top: r.bottom + 6, left: r.left });
  };
  const open = () => {
    clearTimeout(timerRef.current);
    measure();
    setHoverOpen(true);
  };
  const closeSoon = () => { timerRef.current = setTimeout(() => setHoverOpen(false), 160); };
  const socials = editorProfile?.social_links || {};
  const socialIcons = [
    { key: 'instagram', Icon: Instagram, label: 'Instagram' },
    { key: 'facebook', Icon: Facebook, label: 'Facebook' },
    { key: 'tiktok', Icon: Music2, label: 'TikTok' },
    { key: 'youtube', Icon: Youtube, label: 'YouTube' },
    { key: 'website', Icon: Globe, label: 'Website' },
  ].filter((s) => socials[s.key]);

  // Re-measure on scroll/resize so the card tracks the anchor while open.
  useEffect(() => {
    if (!hoverOpen) return;
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [hoverOpen]);

  const card = (hasCard && hoverOpen) ? createPortal(
    <div
      role="tooltip"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
      className="w-72 rounded-xl border border-border bg-card/95 backdrop-blur-md shadow-2xl p-3 text-left"
      onMouseEnter={() => { clearTimeout(timerRef.current); setHoverOpen(true); }}
      onMouseLeave={closeSoon}
      data-testid="author-hover-card"
    >
      <div className="flex items-start gap-3">
        {avatar ? (
          <img src={avatar} alt={name} className="h-12 w-12 rounded-full object-cover shrink-0" />
        ) : (
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-fuchsia-500/30 to-indigo-500/30 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-fuchsia-200" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground truncate">{name}</p>
          <p className="text-[11px] text-fuchsia-300 uppercase tracking-widest font-semibold">Eventism Editor</p>
        </div>
      </div>
      {editorProfile.blurb && (
        <p className="text-xs text-muted-foreground leading-relaxed mt-2">{editorProfile.blurb}</p>
      )}
      {socialIcons.length > 0 && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
          {socialIcons.map(({ key, Icon, label }) => (
            <a
              key={key}
              href={socials[key]}
              target="_blank"
              rel="noopener noreferrer"
              className="h-7 w-7 rounded-full bg-background/70 hover:bg-fuchsia-500/20 border border-border flex items-center justify-center text-muted-foreground hover:text-fuchsia-200 transition-colors"
              aria-label={label}
              data-testid={`author-social-${key}`}
            >
              <Icon className="h-3.5 w-3.5" />
            </a>
          ))}
        </div>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <span
      ref={anchorRef}
      className="inline-flex items-center gap-1.5"
      onMouseEnter={open}
      onMouseLeave={closeSoon}
      onFocus={open}
      onBlur={closeSoon}
      data-testid="author-byline"
    >
      {avatar ? (
        <img
          src={avatar}
          alt={name}
          className="h-5 w-5 rounded-full object-cover border border-border"
          data-testid="author-avatar"
        />
      ) : (
        <Sparkles className="h-3.5 w-3.5 text-fuchsia-300" />
      )}
      <span>By <span className={hasCard ? 'text-foreground font-semibold underline decoration-dotted underline-offset-2 cursor-help' : ''}>{name}</span></span>
      {card}
    </span>
  );
}


// ---------------------------------------------------------------------------
// VenueAddendum — tight labeled list under the photo gallery. Each row is
// icon + bold label + inline value. Only renders enabled fields the admin
// toggled on in the guide editor.
// ---------------------------------------------------------------------------
const ADDENDUM_ICON = {
  location: MapPin,
  best_for: Sparkles,
  views_of: Eye,
  why_recommend: Award,
  people_saying: Quote,
};
const ADDENDUM_LABEL = {
  location: 'Location',
  best_for: 'Best for',
  views_of: 'Views of',
  why_recommend: 'Why we recommend',
  people_saying: 'People are saying',
};

function VenueAddendum({ fields, data, venueId }) {
  const rows = (fields || [])
    .filter((f) => ADDENDUM_LABEL[f] && (data || {})[f])
    .map((f) => ({ key: f, Icon: ADDENDUM_ICON[f], label: ADDENDUM_LABEL[f], value: data[f] }));
  if (rows.length === 0) return null;
  return (
    <ul
      className="space-y-1.5 pt-1"
      data-testid={`venue-addendum-${venueId}`}
    >
      {rows.map(({ key, Icon, label, value }) => (
        <li
          key={key}
          className="flex items-start gap-2 text-sm text-foreground/85 leading-snug"
          data-testid={`venue-addendum-${venueId}-${key}`}
        >
          <Icon className="h-3.5 w-3.5 mt-1 text-fuchsia-300 shrink-0" aria-hidden />
          <p>
            <span className="font-bold text-foreground">{label}:</span>{' '}
            <span className={key === 'people_saying' ? 'italic' : ''}>{value}</span>
          </p>
        </li>
      ))}
    </ul>
  );
}


// Style presets for the admin-defined custom CTA buttons rendered under
// each event card. Keys must stay in sync with the admin editor's
// `variant` dropdown and the backend `_normalize_event_cards` whitelist.
const EVENT_CARD_BUTTON_VARIANTS = {
  primary: 'bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 text-white hover:opacity-90',
  outline: 'border border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10',
  ghost:   'text-fuchsia-200 hover:bg-fuchsia-500/10',
  amber:   'bg-amber-500 text-black hover:bg-amber-400',
  indigo:  'bg-indigo-500 text-white hover:bg-indigo-400',
};


// ---------------------------------------------------------------------------
// EditorialEventCard — mirrors EditorialVenueCard's layout for event guides.
// The admin curates title/description/photos manually. If the card's recipe
// matched a live ticketed event, the next-upcoming event is rendered as a
// gradient banner ABOVE the photo carousel (same conversion pattern as
// venue cards). When nothing matches, the banner is omitted and the card
// still reads as pure editorial content.
// ---------------------------------------------------------------------------
function EditorialEventCard({ card, index, city }) {
  const photos = Array.from(new Set((card.photos || []).filter(Boolean)));
  const nextEvent = card.next_event;
  const nextEventUrl = eventDetailUrl(nextEvent, city);
  const addendumRows = Array.isArray(card.addendum) ? card.addendum : [];
  const vibeTags = (Array.isArray(card.vibe_tags) ? card.vibe_tags : [])
    .filter((v) => v && (v.label || '').trim())
    .slice(0, 7);
  const buttons = (Array.isArray(card.buttons) ? card.buttons : [])
    .filter((b) => b && (b.label || '').trim() && (b.url || '').trim());
  const descriptionHtml = (card.description_html || '').trim();

  return (
    <article
      className="space-y-4"
      data-testid={`event-card-${card.card_id}`}
    >
      {/* Number + title kicker */}
      <div className="flex items-baseline gap-3">
        <span
          className="text-4xl sm:text-5xl font-bold font-['Space_Grotesk'] tracking-tight text-indigo-300/70 tabular-nums"
          aria-hidden="true"
        >
          {String(index).padStart(2, '0')}
        </span>
        <div className="min-w-0 flex-1">
          <h2
            className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] tracking-tight leading-tight"
            data-testid={`event-card-title-${card.card_id}`}
          >
            {card.title || 'Untitled card'}
          </h2>
        </div>
      </div>

      {/* Next matching event banner — ABOVE the photo (mirrors venue card). */}
      {nextEvent && nextEventUrl && (
        <Link
          to={nextEventUrl}
          className="block rounded-xl border border-fuchsia-500/40 bg-gradient-to-r from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15 hover:from-fuchsia-500/25 transition-colors p-3 sm:p-4"
          data-testid={`event-card-ticket-banner-${card.card_id}`}
        >
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm sm:text-base truncate">
                {nextEvent.name}
              </p>
              <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
                <Calendar className="h-3 w-3 inline mr-1 text-fuchsia-300" />
                {formatDate(nextEvent.event_date)}
                {nextEvent.event_time ? ` · ${formatTime(nextEvent.event_time)}` : ''}
                {nextEvent.venue_name ? ` · ${nextEvent.venue_name}` : ''}
              </p>
            </div>
            <BuyTicketsCTA nextEvent={nextEvent} venueId={card.card_id} />
          </div>
        </Link>
      )}

      {/* Photo carousel (single or many). Falls back to a placeholder tile
          so the layout doesn't collapse when the admin hasn't added photos
          yet. */}
      {photos.length > 0 ? (
        <PhotoCarousel
          photos={photos}
          alt={card.title || 'Event card photo'}
          venueId={card.card_id}
          badge={null}
          detailUrl={null}
        />
      ) : (
        <div className="rounded-xl border border-dashed border-border/50 bg-muted/20 aspect-[16/9] flex items-center justify-center">
          <p className="text-xs text-muted-foreground">No photos yet</p>
        </div>
      )}

      {/* Description */}
      {(descriptionHtml || card.description) && (
        <div className="space-y-3">
          {descriptionHtml ? (
            <div
              className="prose prose-invert max-w-none text-base sm:text-lg leading-relaxed text-foreground/85 [&_a]:text-fuchsia-300 [&_a]:underline hover:[&_a]:text-fuchsia-200 [&_p]:mb-3 [&_strong]:text-foreground [&_em]:text-foreground [&_h3]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-2 [&_blockquote]:border-fuchsia-500 [&_blockquote]:pl-4 [&_blockquote]:italic"
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              data-testid={`event-card-desc-${card.card_id}`}
            />
          ) : (
            <p
              className="text-base sm:text-lg leading-relaxed text-foreground/85 whitespace-pre-line"
              data-testid={`event-card-desc-${card.card_id}`}
            >
              {card.description}
            </p>
          )}
        </div>
      )}

      {/* Vibe tags — pill buttons that link to venue/event searches or
          a custom URL per tag. Rendered right under the description. */}
      {vibeTags.length > 0 && (
        <div
          className="flex flex-wrap gap-2"
          data-testid={`event-card-vibes-${card.card_id}`}
        >
          {vibeTags.map((v, i) => {
            const label = (v.label || '').trim();
            const target = ((v.target || '').trim() || label).toLowerCase();
            const cityLc = (city || 'denver').toLowerCase();
            const href =
              v.link_type === 'custom'
                ? (v.target || '#')
                : v.link_type === 'event'
                  ? `/events/${cityLc}/tags/${encodeURIComponent(target)}`
                  : `/${cityLc}/venues?tag=${encodeURIComponent(target)}`;
            const isExternal = v.link_type === 'custom' && /^https?:\/\//i.test(href);
            const className =
              "text-xs font-semibold px-3 py-1.5 rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 transition-colors";
            return isExternal ? (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
                data-testid={`event-card-vibe-${card.card_id}-${i}`}
              >
                {label}
              </a>
            ) : (
              <Link
                key={i}
                to={href}
                className={className}
                data-testid={`event-card-vibe-${card.card_id}-${i}`}
              >
                {label}
              </Link>
            );
          })}
        </div>
      )}

      {/* Custom CTA buttons — variants map to a preset style. External
          URLs open in a new tab automatically when open_in_new_tab is set. */}
      {buttons.length > 0 && (
        <div
          className="flex flex-wrap gap-2"
          data-testid={`event-card-buttons-${card.card_id}`}
        >
          {buttons.map((b, i) => {
            const url = (b.url || '').trim();
            const isExternal = /^https?:\/\//i.test(url);
            const variantClass = EVENT_CARD_BUTTON_VARIANTS[b.variant] || EVENT_CARD_BUTTON_VARIANTS.primary;
            const className = `inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-all ${variantClass}`;
            const newTab = !!b.open_in_new_tab || isExternal;
            return isExternal ? (
              <a
                key={i}
                href={url}
                target={newTab ? '_blank' : undefined}
                rel={newTab ? 'noopener noreferrer' : undefined}
                className={className}
                data-testid={`event-card-button-${card.card_id}-${i}`}
              >
                {b.label}
                {newTab && <ExternalLink className="h-3 w-3" aria-hidden />}
              </a>
            ) : (
              <Link
                key={i}
                to={url}
                target={newTab ? '_blank' : undefined}
                rel={newTab ? 'noopener noreferrer' : undefined}
                className={className}
                data-testid={`event-card-button-${card.card_id}-${i}`}
              >
                {b.label}
                {newTab && <ExternalLink className="h-3 w-3" aria-hidden />}
              </Link>
            );
          })}
        </div>
      )}

      {/* Under-photo custom info — admin-controlled label:value rows.
          Uses the same 5 icons as venue guides when key matches; falls
          back to a generic Sparkles for `custom`. */}
      {addendumRows.length > 0 && (
        <ul
          className="space-y-1.5 pt-1"
          data-testid={`event-card-addendum-${card.card_id}`}
        >
          {addendumRows.map((row, i) => {
            const Icon = ADDENDUM_ICON[row.key] || Sparkles;
            return (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-foreground/85 leading-snug"
                data-testid={`event-card-addendum-${card.card_id}-${i}`}
              >
                <Icon className="h-3.5 w-3.5 mt-1 text-fuchsia-300 shrink-0" aria-hidden />
                <p>
                  <span className="font-bold text-foreground">{row.label}:</span>{' '}
                  <span className={row.key === 'people_saying' ? 'italic' : ''}>{row.value}</span>
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {/* Additional matching events (compact list under the description) */}
      {(card.matching_events || []).length > 1 && (
        <div className="rounded-lg border border-border/50 bg-card/40 p-3 space-y-1.5" data-testid={`event-card-more-${card.card_id}`}>
          <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            More upcoming
          </p>
          <ul className="space-y-1">
            {(card.matching_events || []).slice(1, 5).map((ev) => {
              const url = eventDetailUrl(ev, city);
              const inner = (
                <>
                  <span className="font-semibold truncate">{ev.name}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
                    {formatDate(ev.event_date)}
                  </span>
                </>
              );
              return (
                <li key={ev.event_id} className="text-xs flex items-center justify-between gap-2">
                  {url ? (
                    <Link to={url} className="flex items-center justify-between gap-2 w-full hover:text-fuchsia-300 transition-colors">
                      {inner}
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between gap-2 w-full">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </article>
  );
}


// ---------------------------------------------------------------------------
// PhotoCarousel — horizontally-scrollable rail with snap, prev/next buttons,
// and a per-photo position indicator. Uses native scroll snap so it works
// great on touch without a JS dependency.
// ---------------------------------------------------------------------------
function PhotoCarousel({ photos, alt, venueId, badge, detailUrl }) {
  const railRef = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = railRef.current;
    if (!el) return;
    const w = el.clientWidth;
    setActive(Math.round(el.scrollLeft / w));
  };

  const scrollTo = (i) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * i, behavior: 'smooth' });
  };

  if (!photos || photos.length === 0) {
    const emptyInner = (
      <>
        <Wine className="h-14 w-14 text-fuchsia-300/70" />
        {badge && <PhotoBadge badge={badge} venueId={venueId} />}
      </>
    );
    if (detailUrl) {
      return (
        <Link
          to={detailUrl}
          className="relative aspect-[16/10] rounded-2xl bg-gradient-to-br from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15 flex items-center justify-center hover:opacity-90 transition-opacity"
          data-testid={`venue-photo-empty-${venueId}`}
          aria-label={`View ${alt} profile`}
        >
          {emptyInner}
        </Link>
      );
    }
    return (
      <div
        className="relative aspect-[16/10] rounded-2xl bg-gradient-to-br from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15 flex items-center justify-center"
        data-testid={`venue-photo-empty-${venueId}`}
      >
        {emptyInner}
      </div>
    );
  }

  return (
    <div className="relative group" data-testid={`venue-photo-carousel-${venueId}`}>
      <div
        ref={railRef}
        onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory rounded-2xl scrollbar-hide"
        style={{ scrollbarWidth: 'none' }}
      >
        {photos.map((src, i) => {
          const tile = (
            <img
              src={src}
              alt={`${alt} — photo ${i + 1}`}
              loading={i === 0 ? 'eager' : 'lazy'}
              className="w-full h-full object-cover"
            />
          );
          return (
            <div key={i} className="snap-center shrink-0 w-full aspect-[16/10] bg-card">
              {detailUrl ? (
                <Link
                  to={detailUrl}
                  className="block w-full h-full"
                  data-testid={`venue-photo-link-${venueId}-${i}`}
                  aria-label={`View ${alt} profile`}
                >
                  {tile}
                </Link>
              ) : tile}
            </div>
          );
        })}
      </div>

      {/* Live-offering badge — top left, always visible across all photos */}
      {badge && <PhotoBadge badge={badge} venueId={venueId} />}

      {photos.length > 1 && (
        <>
          {/* Prev / next buttons — visible on hover (desktop) and always on touch */}
          <button
            type="button"
            onClick={() => scrollTo(Math.max(0, active - 1))}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/85 backdrop-blur border border-border shadow-sm hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Previous photo"
            data-testid={`venue-photo-prev-${venueId}`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollTo(Math.min(photos.length - 1, active + 1))}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/85 backdrop-blur border border-border shadow-sm hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Next photo"
            data-testid={`venue-photo-next-${venueId}`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          {/* Dot indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.map((_, i) => (
              <button
                type="button"
                key={i}
                onClick={() => scrollTo(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? 'w-6 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to photo ${i + 1}`}
              />
            ))}
          </div>

          {/* Photo count chip */}
          <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-background/75 backdrop-blur text-[11px] font-semibold tabular-nums border border-border">
            {active + 1} / {photos.length}
          </div>
        </>
      )}
    </div>
  );
}

// Reusable live-offering chip pinned to the top-left of the photo gallery.
// Emerald-tinted so buyers see "there's something to book here" at a glance.
function PhotoBadge({ badge, venueId }) {
  const Icon = badge.icon;
  return (
    <div
      className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/95 text-white text-[11px] font-bold uppercase tracking-widest shadow-lg shadow-emerald-500/30 backdrop-blur-sm border border-emerald-300/30"
      data-testid={`venue-photo-badge-${venueId}`}
    >
      <Icon className="h-3.5 w-3.5" /> {badge.label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OfferingChips — DesignMyNight-style clickable vibe tags that route to a
// tag-filtered search page (so one editorial guide can fan out to dozens).
// Live-offering chips (Events / Experiences / Reservations) are intentionally
// rendered ONLY on the photo top-left badge — duplicating them here would
// create chip noise and dilute the click signal for the tag-search hub.
// ---------------------------------------------------------------------------
function OfferingChips({ city, vibe_tags, amenities, venueId }) {
  // Combine vibe_tags + amenities, dedupe, and cap to a reasonable row.
  const all = [...(vibe_tags || []), ...(amenities || [])];
  const seen = new Set();
  const tags = [];
  for (const t of all) {
    const key = String(t).toLowerCase().trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    tags.push(t);
    if (tags.length >= 6) break;
  }
  if (!tags.length) return null;

  return (
    <div className="flex flex-wrap gap-2 pt-1" data-testid={`venue-tag-row-${venueId}`}>
      {tags.map((t) => (
        <Link
          key={t}
          to={tagSearchUrl(city, t)}
          className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-fuchsia-400/60 hover:bg-fuchsia-500/5 transition-colors"
          data-testid={`venue-tag-${venueId}-${String(t).toLowerCase().replace(/\s+/g, '-')}`}
        >
          {t}
        </Link>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EventCard — compact card for the "Coming up in this city" rail
// ---------------------------------------------------------------------------
function EventCard({ event }) {
  const KIND_URL = { event: 'events', experience: 'ex', reservation: 'res' };
  const seg = KIND_URL[event.kind] || 'events';
  const url = `/${seg}/${event.city || 'denver'}/${event.slug || event.event_id}`;
  return (
    <Link to={url} className="group block" data-testid={`guide-event-${event.event_id}`}>
      <Card className="border-border hover:border-fuchsia-400/50 overflow-hidden h-full">
        <div className="aspect-video bg-card overflow-hidden">
          {event.image_url ? (
            <img
              src={event.image_url}
              alt={event.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15 flex items-center justify-center">
              <Calendar className="h-8 w-8 text-fuchsia-300/70" />
            </div>
          )}
        </div>
        <CardContent className="p-3 space-y-1">
          <p className="font-bold text-sm truncate group-hover:text-fuchsia-300 transition-colors">{event.name}</p>
          <div className="text-[11px] text-muted-foreground flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3 text-fuchsia-400" />
              {formatDate(event.event_date)}
            </span>
            <span className="capitalize">{event.kind || 'event'}</span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
